/**
 * useCameraAccess Hook
 * Manages camera permissions and stream access
 */
import { useState, useCallback, useRef, useEffect } from "react";

interface UseCameraAccessReturn {
  stream: MediaStream | null;
  isActive: boolean;
  error: string | null;
  startCamera: (facingMode?: "user" | "environment") => Promise<void>;
  stopCamera: () => void;
  switchCamera: () => Promise<void>;
  captureImage: () => Blob | null;
  videoRef: React.RefObject<HTMLVideoElement | null>;
}

export const useCameraAccess = (): UseCameraAccessReturn => {
  const [stream, setStream] = useState<MediaStream | null>(null);
  const [isActive, setIsActive] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [currentFacingMode, setCurrentFacingMode] = useState<
    "user" | "environment"
  >("user");
  const videoRef = useRef<HTMLVideoElement>(null);

  const startCamera = useCallback(
    async (facingMode: "user" | "environment" = "user") => {
      try {
        setError(null);

        // Stop existing stream if any
        if (stream) {
          stream.getTracks().forEach((track) => track.stop());
        }

        // Request camera access
        const mediaStream = await navigator.mediaDevices.getUserMedia({
          video: {
            facingMode: facingMode,
            width: { ideal: 1280 },
            height: { ideal: 720 },
          },
          audio: false,
        });

        setStream(mediaStream);
        setCurrentFacingMode(facingMode);
        setIsActive(true);

        // Attach to video element
        if (videoRef.current) {
          videoRef.current.srcObject = mediaStream;
        }
      } catch (err: any) {
        console.error("Camera access error:", err);

        if (err.name === "NotAllowedError") {
          setError(
            "Camera permission denied. Please allow camera access in your browser settings."
          );
        } else if (err.name === "NotFoundError") {
          setError("No camera found. Please connect a camera and try again.");
        } else if (err.name === "NotReadableError") {
          setError("Camera is already in use by another application.");
        } else {
          setError("Failed to access camera. Please try again.");
        }

        setIsActive(false);
      }
    },
    [stream]
  );

  const stopCamera = useCallback(() => {
    if (stream) {
      stream.getTracks().forEach((track) => track.stop());
      setStream(null);
      setIsActive(false);

      if (videoRef.current) {
        videoRef.current.srcObject = null;
      }
    }
  }, [stream]);

  const switchCamera = useCallback(async () => {
    const newFacingMode = currentFacingMode === "user" ? "environment" : "user";
    await startCamera(newFacingMode);
  }, [currentFacingMode, startCamera]);

  const captureImage = useCallback((): Blob | null => {
    if (!videoRef.current || !isActive) return null;

    const video = videoRef.current;
    const canvas = document.createElement("canvas");
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;

    const ctx = canvas.getContext("2d");
    if (!ctx) return null;

    ctx.drawImage(video, 0, 0);

    return new Promise<Blob | null>((resolve) => {
      canvas.toBlob(
        (blob) => {
          resolve(blob);
        },
        "image/jpeg",
        0.9
      );
    }) as any;
  }, [isActive]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (stream) {
        stream.getTracks().forEach((track) => track.stop());
      }
    };
  }, [stream]);

  return {
    stream,
    isActive,
    error,
    startCamera,
    stopCamera,
    switchCamera,
    captureImage,
    videoRef,
  };
};
